import React, { useState, useEffect } from 'react';
import './MerchantStore.css';
import { useNavigate, useParams, Link } from 'react-router-dom';
import backArrowIcon from '../../assets/backarrow-icon.png';
import dotsIcon from '../../assets/dots-icon.png';
import dotsHoriIcon from '../../assets/dots-hori-icon.png';
import editIcon from '../../assets/edit-icon.png';
import deleteIcon from '../../assets/delete-outline-icon.png';
import likeIcon from '../../assets/like-icon.png';
import likeIconRed from '../../assets/like-icon-red.png';
import placeholderLogo from '../../assets/placeholder-logo.png';
import placeholderBanner from '../../assets/placeholder-banner.png';
import ReadMoreText from '../../common/ReadMoreText';

import { useMerchantStore } from '../../context/MerchantStoreContext';
import { useLikedProducts } from '../../context/LikedProductsContext';
import MerchantStoreSkeleton from '../../components/skeleton/MerchantStoreSkeleton';
import PromoteProductDrawer from '../../components/popups/PromoteProductDrawer';
import DeleteProductDrawer from '../../components/popups/DeleteProductDrawer';
import MerchantStoreMenuDrawer from '../../components/popups/MerchantStoreMenuDrawer';
import bluetickIcon from '../../assets/bluetick-icon.png';

import api from '../../api';




function MerchantStore({ user }) {
  const [store, setStore] = useState(null);
  const [showDropdown, setShowDropdown] = useState(null);
  const [savedProducts, setSavedProducts] = useState([]);
  const [piToUsdRate, setPiToUsdRate] = useState(null);
  const [showDynamicInfo, setShowDynamicInfo] = useState(false);
  const [showPromotePopup, setShowPromotePopup] = useState(null);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const navigate = useNavigate();
  const { businessUsername } = useParams();
  const [storeNotFound, setStoreNotFound] = useState(false);
  const [showDeleteDrawer, setShowDeleteDrawer] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null); // {_id, name}
  const [metrics, setMetrics] = useState(null);       // ✅ NEW
  const [metricsLoading, setMetricsLoading] = useState(false); // ✅ NEW
  const [showStoreMenuDrawer, setShowStoreMenuDrawer] = useState(false);
  const [activeSalesMap, setActiveSalesMap] = useState({});


// screen-only toast (success | error)
const [toast, setToast] = useState({ visible: false, message: '', type: '' });
const showToast = (message, type = 'success') => {
  setToast({ visible: true, message, type });
  setTimeout(() => setToast({ visible: false, message: '', type: '' }), 2200);
};



 const {
   products,
   setProducts,
   getStoreMetrics,
   upsertStoreMetrics,
 } = useMerchantStore();

  const { likedProducts, toggleLike, setLikedProducts: setGlobalLikedProducts } = useLikedProducts();

  useEffect(() => {
    if (user?.businessUsername && businessUsername) {
      const match = user.businessUsername === businessUsername;
      setIsOwner(match);
    }
  }, [user?.businessUsername, businessUsername]);

  useEffect(() => {
    console.log('🧩 Params businessUsername:', businessUsername);
    console.log('🧍 Current user:', user?.username, '| businessUsername:', user?.businessUsername);
    console.log('🏪 Expected store:', store?.businessUsername);
    console.log('🔍 isOwner:', isOwner);
  }, [businessUsername, user, store, isOwner]);



// This is your existing code that fetches store data
const fetchStore = async () => {
  if (!businessUsername) return;
  try {
    const storeRes = await api.get(`/stores/by-business-username/${businessUsername}`);
    setStore(storeRes.data);
    setStoreNotFound(false); // clear fallback if store exists
    console.log("Store Data after fetch:", storeRes.data);

    // ✅ Track store visit (only if not owner)
    if (user && user._id && String(storeRes.data.owner) !== String(user._id)) {
      try {
        await api.post(`/stores/visit/${storeRes.data._id}`, {
          userId: user._id,
        });
        console.log('✅ Store visit logged');
      } catch (visitErr) {
        console.warn('⚠️ Failed to log store visit:', visitErr?.response?.data || visitErr.message);
      }
    }

  } catch (err) {
    console.error('❌ Error fetching store:', err.response?.data || err.message);
    if (err.response?.status === 404) {
      setStore(null);
      setStoreNotFound(true); // 🧱 set fallback state for missing store
    }
  }
};



// ✅ Fetch metrics (cached-first via context) once we know the store owner username
useEffect(() => {
  const run = async () => {
    if (!store?.username) return;

    // 1) try cached metrics first (default TTL = 5 minutes inside context)
    const cached = getStoreMetrics(store.username);
    if (cached) {
      setMetrics({ avgRating: cached.avgRating, completedOrders: cached.completedOrders });
      setMetricsLoading(false);
      return;
    }

    // 2) otherwise fetch and cache
    try {
      setMetricsLoading(true);
      const res = await api.get(`/merchants/metrics/${store.username}`);
      const next = {
        avgRating: Number(res.data?.avgRating ?? 0),
        completedOrders: Number(res.data?.completedOrders ?? 0),
      };
      setMetrics(next);
      upsertStoreMetrics(store.username, next);
    } catch (err) {
      console.error('❌ Failed to fetch merchant metrics:', err);
      setMetrics({ avgRating: 0, completedOrders: 0 });
    } finally {
      setMetricsLoading(false);
    }
  };

  run();
}, [store?.username, getStoreMetrics, upsertStoreMetrics]);


useEffect(() => {
  const fetchActiveSales = async () => {
    // We need the seller's username to query their sales
    const sellerUsername = store?.username || store?.businessUsername;
    if (!sellerUsername) return;

    try {
      const res = await api.get(
        `/merchants/me/flash-sales?status=active&username=${encodeURIComponent(sellerUsername)}`
      );
      const sales = res.data?.sales || [];
      const map = {};
      sales.forEach(s => {
        // Map productId -> { percentOff, endAt, saleId }
        map[String(s.productId)] = {
          percentOff: Number(s.percentOff),
          endAt: s.endAt,
          saleId: s._id
        };
      });
      setActiveSalesMap(map);
    } catch (err) {
      console.warn('⚠️ Failed to fetch active flash sales for store:', err?.response?.data || err.message);
    }
  };
  fetchActiveSales();
}, [store?.username, store?.businessUsername]);



  const fetchProducts = async () => {
    if (!businessUsername) return;
    try {

     const res = await api.get(`/products/by-business-username/${businessUsername}?username=${user?.username || ''}`);
 
     const allProducts = res.data || [];
      setProducts(allProducts.filter(p => !p.isHidden));

      const rateRes = await api.get('/convert/rate');
      if (rateRes.data?.piToUsd) {
        setPiToUsdRate(rateRes.data.piToUsd);
      }

 // 1) Prefer server boolean, same as ProductDetailScreen
 const fromServer = allProducts
   .filter(p => p.userHasLiked === true)
   .map(p => p._id);

 // 2) Fallback that tolerates different shapes for likes/saves (ids, usernames, objects)
 const uid = user?._id;
 const uname = user?.username;
 const coerceHasMe = (arr = []) => Array.isArray(arr) && arr.some(v => {
   if (!v) return false;
   if (typeof v === 'string') return v === uid || v === uname;
   if (typeof v === 'object') return v._id === uid || v.id === uid || v.username === uname;
   return false;
 });

 const fallbackLiked = fromServer.length
   ? fromServer
   : allProducts.filter(p => coerceHasMe(p.likes) || coerceHasMe(p.likedBy)).map(p => p._id);

 const fallbackSaved = allProducts.filter(p => coerceHasMe(p.saves) || coerceHasMe(p.savedBy)).map(p => p._id);

 // ✅ Seed the global liked set that the grid actually reads
 setGlobalLikedProducts(fallbackLiked);
 setSavedProducts(fallbackSaved);
    } catch (err) {
      console.error('❌ Failed to load products:', err);
    }
  };

  useEffect(() => {
    fetchStore();
  }, [businessUsername]);

  useEffect(() => {
    fetchProducts();
  }, [businessUsername]);

  const handleEdit = (productId) => {
    navigate(`/edit-product/${productId}`);
  };



// ⬇️ Replace your old handleDelete with this opener
const handleDelete = (productOrId) => {
  // Accepts either a product object or an _id string
  const product =
    typeof productOrId === 'string'
      ? products.find(p => p._id === productOrId)
      : productOrId;

  setShowDropdown(null);
  setProductToDelete(product ? { _id: product._id, name: product.name } : { _id: productOrId, name: '' });
  setShowDeleteDrawer(true);
};

// ⬇️ Call this from the drawer's "Delete" button
const confirmDeleteProduct = async () => {
  if (!productToDelete?._id) return;

  try {
    await api.delete(`/products/${productToDelete._id}`);

    // Optimistic UI update
    setProducts(prev => prev.filter(p => p._id !== productToDelete._id));

    // Close drawer + toast
    setShowDeleteDrawer(false);
    setProductToDelete(null);
    showToast(' Product deleted.', 'success');

    // Optional re-fetch for server truth
    await fetchProducts();
  } catch (err) {
    const status = err?.response?.status;
    const data = err?.response?.data;

    if (status === 409) {
      const count = data?.pendingCount;
      showToast(
        count != null
          ? ` Cannot delete: this product has ${count} pending escrow order${count === 1 ? '' : 's'}.`
          : ' Cannot delete: this product has pending escrow orders.',
        'error'
      );
    } else if (status === 404) {
      showToast('⚠️ Product not found — it may have already been deleted.', 'error');
    } else {
      showToast('❌ Failed to delete product. Please try again.', 'error');
    }

    setShowDeleteDrawer(false);
    setProductToDelete(null);
    await fetchProducts();
  }
};




  const handleAddProduct = () => {
    navigate('/add-product');
  };

  const toggleSave = async (productId) => {
    try {
      await api.post(`/products/${productId}/save`, { username: user.username });
      setSavedProducts(prev =>
        prev.includes(productId) ? prev.filter(id => id !== productId) : [...prev, productId]
      );
    } catch (err) {
      console.error('❌ Save toggle failed:', err);
    }
  };


const basePi = (p) => {
  if (p?.pricingType === 'dynamic' && piToUsdRate && p?.usdPrice != null) {
    return +(p.usdPrice / piToUsdRate).toFixed(2);
  }
  return +Number(p?.price || 0).toFixed(2);
};

const saleViewFor = (p) => {
  const sale = activeSalesMap[String(p._id)];
  if (!sale) return null;

  const original = basePi(p);
  const percent = Number(sale.percentOff || 0);
  if (!Number.isFinite(original) || !Number.isFinite(percent)) return null;

  const discounted = +Number(original * (1 - percent / 100)).toFixed(2);
  return { original, discounted, percent, endAt: sale.endAt };
};


  return (
    <div className="merchant-store-container">
      {/* ✅ Topbar */}
      <div className="merchantstore-topbar">
        <img
          src={backArrowIcon}
          alt="Back"
          className="merchantstore-back-icon"
          onClick={() => navigate('/marketplace', { replace: true })}
        />
        <h2 className="merchantstore-title">
          {isOwner
            ? 'My Store'
            : store?.businessName?.trim()
            ? store.businessName
            : store?.businessUsername
            ? `${store.businessUsername}'s Store`
            : 'Loading...'}
        </h2>


{isOwner && (
  <div className="merchantstore-dots-wrapper">
    <button
      type="button"
      className="merchantstore-menu-btn"
      onClick={() => setShowStoreMenuDrawer(true)}
      aria-label="Open store menu"
    >
      <img
        src={dotsIcon}
        alt="menu"
        className="merchantstore-dots-icon"
      />
    </button>
  </div>
)}
          </div>


{/* ✅ Store Not Found Message (insert this just below topbar) */}
    {storeNotFound && (
      <div className="merchantstore-notfound">
        <h2>🛍️ Store Not Found</h2>
        <p>This merchant is no longer active or the store has been removed.</p>
        <button className="go-back-btn" onClick={() => navigate('/marketplace')}> Go Back</button>
      </div>
    )}



      {/* ✅ Scrollable content */}
      <div className="merchantstore-scroll-content">
        {store ? (
          <>
            {store.status === 'suspended' && (
              <div className="merchantstore-banner-suspended">
                ⚠️ This merchant’s account is currently <strong>suspended</strong>.
              </div>
            )}

            <div className="merchantstore-banner-wrapper">
              <img
                src={store.bannerImage || placeholderBanner}
                alt="Store Banner"
                className="merchantstore-banner"
              />
              <div className="merchantstore-logo-wrapper">
                <img
                  src={store.businessLogo || placeholderLogo}
                  alt="Store Logo"
                  className="merchantstore-logo"
                />
              </div>
            </div>

            <div className="merchantstore-info">


<h3 className="store-display-name">
  <span className="store-display-name-text">
    {store.businessName || store.storeName}
  </span>
  <img
    src={bluetickIcon}
    alt="Verified business"
    className="merchantstore-verified-icon"
  />
</h3>



              <p className="store-username">@{store.businessUsername || store.username}</p>
              {store.location && <p className="merchantstore-location">{store.location}</p>}


<p className="merchantstore-meta">
  Owner:{' '}
  <Link to={`/user/${store.username}`} className="store-owner-link">
    {store.name || store.username}
  </Link>
  <br />
  Since: {new Date(store.createdAt).toLocaleDateString()}
  <br />


{/* ✅ Average Rating line */}
{metricsLoading ? (
  <>Average Rating: …</>
) : metrics?.avgRating != null ? (
  <>Average Rating: {Number(metrics.avgRating).toFixed(1)} <span className="store-star" aria-hidden>★</span></>
) : (
  <>Average Rating: 0 <span className="store-star" aria-hidden>★</span></>
)}
<br />
{/* ✅ Completed orders (from metrics) */}
{metricsLoading ? (
  <>Completed Orders: …</>
) : metrics?.completedOrders != null ? (
  <>Completed Orders: {metrics.completedOrders}</>
) : (
  <>Completed Orders: 0</>
)}

</p>

              <ReadMoreText
                text={store.description}
                maxLines={3}
                className="merchantstore-description"
              />
            </div>

            {products.length === 0 ? (
              <p className="no-products-text">No products yet.</p>
            ) : (
              <div className="merchantstore-grid">
                {products.map(product => (
                  <React.Fragment key={product._id}>
                    <Link to={`/product/${product._id}`} className="product-card-link">
                      <div className="product-card">
                        {isOwner && (
                          <div className="product-dots-wrapper">
                            <img
                              src={dotsHoriIcon}
                              alt="Options"
                              className="product-dots-icon"
                              onClick={(e) => {
                                e.preventDefault();
                                setShowDropdown(showDropdown === product._id ? null : product._id);
                              }}
                            />
                          </div>
                        )}

<div className="product-image-wrapper">
  {/* SALE PILL OVERLAY (top-left) */}
  {(() => {
    const v = saleViewFor(product);
    return v ? (
      <span className="sale-pill left" aria-label={`${v.percent}% off`}>
        -{v.percent}%
      </span>
    ) : null;
  })()}
  <img
    src={product.media?.[0] || 'https://via.placeholder.com/150'}
    onError={(e) => (e.target.src = 'https://via.placeholder.com/150')}
    alt={product.name}
    className="product-image"
  />
</div>


                        <div className="merchantstore-details">






<h4
  className="merchantstore-title"
  title={product.name}
>
  {product?.name
    ? (product.name.length > 15 ? product.name.slice(0, 15) + '…' : product.name)
    : ''}
</h4>


<div className="merchantstore-price-container">
  {(() => {
    const v = saleViewFor(product);
    const value = v ? v.discounted : basePi(product);
    return (
      <p className={`merchantstore-price ${v ? 'price-sale' : ''}`}>
        {value.toFixed(2)} Pi
      </p>
    );
  })()}
</div>


                        </div>
                        {!isOwner && (
                          <div className="product-actions-wrapper">
                            <img
                              src={likedProducts.includes(product._id) ? likeIconRed : likeIcon}
                              alt="Like"
                              className="product-like-icon"
                              onClick={(e) => {
                                e.preventDefault();
                                toggleLike(product._id, api, user.username);
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </Link>

                    {isOwner && showDropdown === product._id && (
                      <div className="product-drawer-popup" onClick={() => setShowDropdown(null)}>
                        <div className="product-drawer-content" onClick={(e) => e.stopPropagation()}>
                          <div className="drawer-item" onClick={(e) => {
                            e.preventDefault(); e.stopPropagation(); handleEdit(product._id);
                          }}>
                            <img src={editIcon} alt="Edit" /> Edit
                          </div>


<div className="drawer-item delete" onClick={(e) => {
  e.preventDefault(); e.stopPropagation();
  handleDelete(product);
}}>
  <img src={deleteIcon} alt="Delete" /> Delete
</div>


                          <div className="drawer-item promote" onClick={(e) => {
                            e.preventDefault(); e.stopPropagation();
                            setShowPromotePopup(product._id); setShowDropdown(null);
                          }}>
                            <img src={require('../../assets/campaign-icon.png')} alt="Promote" /> Promote
                          </div>
                        </div>
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
          </>
        ) : (
          <MerchantStoreSkeleton />
        )}
      </div>


{isOwner && user.status !== 'suspended' && user.isMerchant && (
  <button className="floating-add-product-btn" onClick={handleAddProduct}>
    + Add New Product
  </button>
)}



      {isOwner && user.status === 'suspended' && (
        <div className="merchantstore-suspended-note">
          <p>Your merchant account has been suspended. You cannot add new products.</p>
          <button className="appeal-btn" onClick={() => navigate('/appeal')}>Appeal Suspension</button>
        </div>
      )}

      {showDynamicInfo && (
        <div className="dynamic-popup-backdrop" onClick={() => setShowDynamicInfo(false)}>
          <div className="dynamic-popup-content" onClick={(e) => e.stopPropagation()}>
            <h3>Dynamic Pricing</h3>
            <p>
              This product/service uses <strong>dynamic pricing</strong>, which updates the
              Pi value based on a fixed USD price.
            </p>
            <button onClick={() => setShowDynamicInfo(false)}>Close</button>
          </div>
        </div>
      )}



<PromoteProductDrawer
  visible={!!showPromotePopup}
  onClose={() => {
    setShowPromotePopup(null);
    setAcceptTerms(false);
  }}


onSubmit={async () => {
  try {
    await api.post('/sponsorships/submit', {
      productId: showPromotePopup,
      username: user.username
    });

    setShowPromotePopup(null);
    setAcceptTerms(false);
    showToast(' Submitted for promotion review', 'success');
  } catch (err) {
    const msg = err.response?.data?.message || '❌ Failed to submit for promotion';
    showToast(msg, 'error');
  }
}}


  acceptTerms={acceptTerms}
  setAcceptTerms={setAcceptTerms}
/>


<DeleteProductDrawer
  isOpen={showDeleteDrawer}
  productName={productToDelete?.name}
  onClose={() => {
    setShowDeleteDrawer(false);
    setProductToDelete(null);
  }}
  onConfirm={confirmDeleteProduct}
/>



{toast.visible && (
  <div className={`merchant-toast ${toast.type}`}>
    {toast.message}
  </div>
)}

<MerchantStoreMenuDrawer
  isOpen={showStoreMenuDrawer}
  onClose={() => setShowStoreMenuDrawer(false)}
  onEditStore={() => navigate('/edit-store', { replace: true })}
  onViewDashboard={() => navigate('/merchant/dashboard')}
/>

    </div>
  );
}

export default MerchantStore;
